import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// "light" is the default (cream paper + teal accent).
// Paper / dark / silver remain selectable from the user menu's theme picker.
export type Theme = 'paper' | 'light' | 'dark' | 'silver';

interface ThemeState {
  theme: Theme;
  setTheme: (t: Theme) => void;
}

const VALID: ReadonlySet<Theme> = new Set(['paper', 'light', 'dark', 'silver']);
const DEFAULT_THEME: Theme = 'light';

// Bump this when we want to force-reset all existing users to the new default.
// v2 (2026-05-27): light promoted to default. Any persisted v1 (paper-default)
// preference is wiped so existing users land on light. They can switch back
// from the theme picker if they want.
const PERSIST_VERSION = 2;
const PERSIST_KEY = 'worksnip.theme';

function applyTheme(theme: Theme) {
  if (typeof document === 'undefined') return;
  document.documentElement.dataset.theme = theme;
}

export const useTheme = create<ThemeState>()(
  persist(
    (set) => ({
      theme: DEFAULT_THEME,
      setTheme: (t) => {
        applyTheme(t);
        set({ theme: t });
      },
    }),
    {
      name: PERSIST_KEY,
      version: PERSIST_VERSION,
      // Anything persisted at an older version is discarded — user lands on
      // paper, then can re-pick from the switcher.
      migrate: () => ({ theme: DEFAULT_THEME }),
      onRehydrateStorage: () => (state) => {
        if (state && VALID.has(state.theme)) applyTheme(state.theme);
        else applyTheme(DEFAULT_THEME);
      },
    }
  )
);

// Apply on module load so first paint is correct (before React renders).
// Honour persist version: if storage is older than PERSIST_VERSION, wipe it
// here too so the inline-script pre-React paint already shows paper.
if (typeof document !== 'undefined') {
  try {
    const raw = localStorage.getItem(PERSIST_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      const ver = typeof parsed?.version === 'number' ? parsed.version : 0;
      if (ver < PERSIST_VERSION) {
        localStorage.removeItem(PERSIST_KEY);
        applyTheme(DEFAULT_THEME);
      } else {
        const t = parsed?.state?.theme as Theme | undefined;
        if (t && VALID.has(t)) applyTheme(t);
        else applyTheme(DEFAULT_THEME);
      }
    } else {
      applyTheme(DEFAULT_THEME);
    }
  } catch {
    applyTheme(DEFAULT_THEME);
  }
}
